import { Auth0Client, User } from "@auth0/auth0-spa-js";
import AppEnv from "../utils/AppEnv";
import {
  getRedirectionPathnameAfterLogin,
  IS_WIDGET_ENABLED,
} from "utils/WidgetUtil";
import { getOrganizationFromHostname, isLogoutPage } from "utils/Auth0Utils";
import { parseEntitlementsFromToken } from "./EntitlementService";

let loggedIn = false;
let logoutSupported = false;
let user: any = null;
let isUserLoggedOut = false;

export const CLIENT_ID = AppEnv.AUTH0_CLIENT_ID;
export const DOMAIN = AppEnv.AUTH0_DOMAIN;
export const AUDIENCE =
  AppEnv.AUTH0_AUDIENCE || "https://api.smartmerchandiser.com/";

const ORGANIZATION = getOrganizationFromHostname();

const redirectToLogin = async (isFromErrorPage?: boolean) => {
  await auth0.loginWithRedirect({
    authorizationParams: {
      redirect_uri:
        window.location.origin +
        `${getRedirectionPathnameAfterLogin()}?auth0_redirect=signin`,
      audience: AUDIENCE,
      organization: ORGANIZATION,
      prompt: isFromErrorPage ? "login" : undefined,
    },
  });
};

export const auth0 = new Auth0Client({
  domain: DOMAIN,
  clientId: CLIENT_ID,
});

export const initAuth0Service = (
  _code: string,
  userDetails: User,
  _token: string,
) => {
  user = userDetails;
  loggedIn = true;
  logoutSupported = false;
};

export const initUserService = async () => {
  const auth0Redirect = getAuth0RedirectQueryParam();
  if (auth0Redirect === "signin") {
    try {
      await auth0.handleRedirectCallback();
      user = await auth0.getUser();
      window.history.replaceState(
        null,
        "",
        window.location.origin +
          window.location.pathname +
          window.location.hash,
      );
      loggedIn = true;
    } catch (error: any) {
      console.log("error", error);
      handleAuth0Error(error);
    }
  } else {
    try {
      try {
        await auth0.getTokenSilently();
      } catch (error: any) {
        if (error.error !== "login_required") {
          isUserLoggedOut = true;
          // Don't redirect to login if user is on logout/auth0-error page
          const isOnLogoutOrAuth0ErrorPage = isLogoutPage();
          if (!isOnLogoutOrAuth0ErrorPage) {
            await redirectToLogin();
          }
        }
      }
      user = await auth0.getUser();
      loggedIn = true;
    } catch (err) {
      console.error(err);
      loggedIn = false;
    }
  }

  logoutSupported = IS_WIDGET_ENABLED ? false : true;
};

const getAuth0RedirectQueryParam = () => {
  const queryString = window.location.search;
  if (queryString) {
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get("auth0_redirect");
  } else {
    return null;
  }
};

export const isLoggedIn = () => loggedIn;

export const userLoggedOut = () => isUserLoggedOut;

export const login = async (isFromErrorPage?: boolean) => {
  await redirectToLogin(isFromErrorPage);
};

export const logout = async () => {
  await auth0.logout({
    logoutParams: {
      returnTo: window.location.origin + "/#/logout",
    },
  });
};

export const getUser = async () => await auth0.getUser();

export const isLogoutSupported = () => logoutSupported;

export const getUserId = () => user?.sub;

async function fetchToken() {
  return await auth0.getTokenSilently({
    authorizationParams: {
      audience: AUDIENCE,
    },
  });
}

export const getToken = async () => {
  try {
    return await fetchToken();
  } catch (error: any) {
    if (error.error === "login_required" && user?.sub) {
      await auth0.loginWithPopup({
        authorizationParams: {
          audience: "https://api.smartmerchandiser.com/",
          prompt: "login",
        },
      });
      user = await auth0.getUser();
      loggedIn = true;
      return await fetchToken();
    }
  }
};

export const getUserName = () => user?.name;

export const getUserEmail = () => user?.email;

export const getUserPicture = () => user?.picture;

export const isProfileAccessible = () => false;

export const getEntitlements = async (): Promise<Record<string, string[]>> => {
  if (!user) return {};

  try {
    const token = await getToken();
    if (!token) return {};

    const parsed = parseEntitlementsFromToken(token);

    if (parsed.hasWildcardAccount) {
      return {};
    }

    const result = {};
    parsed.byAccount.forEach((entitlements, accountId) => {
      result[accountId] = Array.from(entitlements);
    });

    return result;
  } catch (error) {
    console.error("Error extracting entitlements:", error);
    return {};
  }
};

export const handleAuth0Error = async (error: any): Promise<void> => {
  const errorType = error?.error || error?.Error || "unknown";
  const errorMessage = error?.error_description || "Unknown error";
  if (errorType === "access_denied") {
    const encodedErrorMessage = encodeURIComponent(errorMessage);
    window.location.hash = `/auth0-error?error_description=${encodedErrorMessage}`;
    return;
  } else {
    window.location.hash = "/auth0-error";
    return;
  }
};
